import type React from 'react'
import { useEffect } from 'react'
import type { PropertiesPanelSectionId } from '@/lib/panels/types/properties-panel-section-id'
import type { ShapeType } from '@/lib/shapes/types/shape-type'
import { areGuideSettingsDialogShortcutsDisabled } from '@/lib/keyboard/dialog-shortcuts'
import { resolveToolShortcut } from '../helpers/resolve-tool-shortcut'

interface UseToolShortcutsInput {
  isPanModeActive: boolean
  selectedShape: ShapeType | null
  selectedPenTool: string | null
  setSelectedShape: (shape: ShapeType | null) => void
  setSelectedPenTool: (tool: string | null) => void
  resetShapeCreation: () => void
  activateTool: (item: string) => void
  openSelectToolSection: (id: PropertiesPanelSectionId) => void
  revealPanel: () => void
  selectToolButtonRef: React.RefObject<HTMLButtonElement | null>
  typeToolButtonRef: React.RefObject<HTMLButtonElement | null>
}

/** Window keydown shortcuts for the left rail's tools — see `resolveToolShortcut`. */
export function useToolShortcuts({
  isPanModeActive,
  selectedShape,
  selectedPenTool,
  setSelectedShape,
  setSelectedPenTool,
  resetShapeCreation,
  activateTool,
  openSelectToolSection,
  revealPanel,
  selectToolButtonRef,
  typeToolButtonRef,
}: UseToolShortcutsInput) {
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (areGuideSettingsDialogShortcutsDisabled()) return

      const shortcut = resolveToolShortcut(e, {
        isPanModeActive,
        hasShapeCreationState: !!(selectedShape || selectedPenTool),
      })

      switch (shortcut) {
        case 'exit-shape-creation':
          e.preventDefault()
          resetShapeCreation()
          activateTool('select')
          return

        case 'select':
          e.preventDefault()
          resetShapeCreation()
          activateTool('select')
          openSelectToolSection('object')
          revealPanel()

          // Close right panel when activating Select tool via v/V so only the object properties panel is open
          window.dispatchEvent(new Event('select-tool-activated'))
          // Move focus to the Select tool button so it is visually active
          setTimeout(() => selectToolButtonRef.current?.focus(), 0)
          return

        case 'type':
          e.preventDefault()
          resetShapeCreation()
          activateTool('type')
          revealPanel()

          setTimeout(() => typeToolButtonRef.current?.focus(), 0)
          return

        case 'pen':
          e.preventDefault()
          setSelectedShape(null)
          setSelectedPenTool('pen')
          activateTool('shape')
          revealPanel()
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [
    activateTool,
    isPanModeActive,
    openSelectToolSection,
    selectedShape,
    setSelectedShape,
    selectedPenTool,
    setSelectedPenTool,
    resetShapeCreation,
    revealPanel,
    selectToolButtonRef,
    typeToolButtonRef,
  ])
}
